import { useQuery, useMutation, useQueryClient } from "react-query";
import { toast } from "sonner";
import { useApi } from "../useApi";

interface BusinessHoursRule {
  dayOfWeek: number;
  startHour: number;
  startMinute?: number; // Optional for backward compatibility
  endHour: number;
  endMinute?: number; // Optional for backward compatibility
  enabled: boolean;
}

interface BusinessHoursConfig {
  enabled: boolean;
  rules: BusinessHoursRule[];
}

interface BusinessHoursResponse {
  id: string;
  tenantId: string;
  enabled: boolean;
  rules: BusinessHoursRule[];
  createdAt: string;
  updatedAt: string;
}

const BUSINESS_HOURS_QUERY_KEY = "business-hours";

export const useGetBusinessHours = (enabled: boolean = true) => {
  const { api } = useApi();
  return useQuery<BusinessHoursResponse>(
    [BUSINESS_HOURS_QUERY_KEY],
    async () => {
      const response = await api.get("/platform/business-hours");
      return response.data;
    },
    {
      enabled,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );
};

export const useUpdateBusinessHours = () => {
  const queryClient = useQueryClient();
  const { api } = useApi();

  return useMutation<BusinessHoursResponse, Error, BusinessHoursConfig>(
    async (config: BusinessHoursConfig) => {
      const response = await api.put("/platform/business-hours", config);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([BUSINESS_HOURS_QUERY_KEY]);
      },
      onError: (error) => {
        console.error("Failed to update business hours:", error);
        toast.error("Failed to update business hours. Please try again.");
      },
    }
  );
};
